import type { Notification } from "@app/shared/atoms/notifications-atom";
import { getCurrentLocaleCode, trans } from "@mongez/localization";

/**
 * Per-type localized title + message templates.
 *
 * Notifications are stored language-agnostic (the backend persists an English
 * title/message only as a fallback / for Telegram). The viewer's locale decides
 * the rendered text, derived from `type` + `metadata` placeholders here.
 */
const TITLE_KEYS: Record<string, string> = {
  // student
  invoiceApproved: "notifications.types.invoiceApproved",
  newLesson: "notifications.types.newLesson",
  newShort: "notifications.types.newShort",
  newOffer: "notifications.types.newOffer",
  newQuiz: "notifications.types.newQuiz",
  newAnnouncement: "notifications.types.newAnnouncement",
  quizResultReady: "notifications.types.quizResultReady",
  liveSessionReminder: "notifications.types.liveSessionReminder",
  subscriptionExpiryReminder: "notifications.types.subscriptionExpiryReminder",
  trainingGroundReviewDue: "notifications.types.trainingGroundReviewDue",
  trainingGroundQuestionFeedbackResolved:
    "notifications.types.trainingGroundQuestionFeedbackResolved",
  gamificationLevelUp: "notifications.types.gamificationLevelUp",
  gameUnlocked: "notifications.types.gameUnlocked",
  goldStorePresentDelivered: "notifications.types.goldStorePresentDelivered",
  goldStorePresentCancelled: "notifications.types.goldStorePresentCancelled",
  gamificationLeagueResult: "notifications.types.gamificationLeagueResult",
  leagueMonthResult: "notifications.types.leagueMonthResult",
  leagueYearResult: "notifications.types.leagueYearResult",
  leaguePassed: "notifications.types.leaguePassed",
  leagueLastDay: "notifications.types.leagueLastDay",
  certificateIssued: "notifications.types.certificateIssued",
  // admin (a dual-role account may receive these on web too)
  invoiceCreated: "notifications.types.invoiceCreated",
  newComment: "notifications.types.newComment",
  contactMessage: "notifications.types.contactMessage",
  newStudentSignup: "notifications.types.newStudentSignup",
  quizNeedsReview: "notifications.types.quizNeedsReview",
  contactReplied: "notifications.types.contactReplied",
  inquiryAdminReply: "notifications.types.inquiryAdminReply",
  communityModerationPending: "notifications.types.communityModerationPending",
  communityContentApproved: "notifications.types.communityContentApproved",
  communityContentRejected: "notifications.types.communityContentRejected",
  communityCommentPublished: "notifications.types.communityCommentPublished",
  communityNewPost: "notifications.types.communityNewPost",
};

const MESSAGE_KEYS: Record<string, string> = {
  invoiceApproved: "notifications.messages.invoiceApproved",
  newLesson: "notifications.messages.newLesson",
  newShort: "notifications.messages.newShort",
  newOffer: "notifications.messages.newOffer",
  newQuiz: "notifications.messages.newQuiz",
  newAnnouncement: "notifications.messages.newAnnouncement",
  quizResultReady: "notifications.messages.quizResultReady",
  liveSessionReminder: "notifications.messages.liveSessionReminder",
  subscriptionExpiryReminder:
    "notifications.messages.subscriptionExpiryReminder",
  trainingGroundReviewDue: "notifications.messages.trainingGroundReviewDue",
  trainingGroundQuestionFeedbackResolved:
    "notifications.messages.trainingGroundQuestionFeedbackResolved",
  gamificationLevelUp: "notifications.messages.gamificationLevelUp",
  gameUnlocked: "notifications.messages.gameUnlocked",
  goldStorePresentDelivered: "notifications.messages.goldStorePresentDelivered",
  goldStorePresentCancelled: "notifications.messages.goldStorePresentCancelled",
  gamificationLeagueResult: "notifications.messages.gamificationLeagueResult",
  leagueMonthResult: "notifications.messages.leagueMonthResult",
  leagueYearResult: "notifications.messages.leagueYearResult",
  leaguePassed: "notifications.messages.leaguePassed",
  leagueLastDay: "notifications.messages.leagueLastDay",
  certificateIssued: "notifications.messages.certificateIssued",
  invoiceCreated: "notifications.messages.invoiceCreated",
  newComment: "notifications.messages.newComment",
  contactMessage: "notifications.messages.contactMessage",
  newStudentSignup: "notifications.messages.newStudentSignup",
  quizNeedsReview: "notifications.messages.quizNeedsReview",
  contactReplied: "notifications.messages.contactReplied",
  inquiryAdminReply: "notifications.messages.inquiryAdminReply",
  communityModerationPending:
    "notifications.messages.communityModerationPending",
  communityContentApproved: "notifications.messages.communityContentApproved",
  communityContentRejected: "notifications.messages.communityContentRejected",
  communityCommentPublished:
    "notifications.messages.communityCommentPublished",
  communityNewPost: "notifications.messages.communityNewPost",
};

/** League month / year notifications carry the period label in both languages. */
function leagueLabel(meta: Record<string, any>): string {
  const label =
    getCurrentLocaleCode() === "ar" ? meta.label : (meta.labelEn ?? meta.label);

  return typeof label === "string" ? label : "";
}

/**
 * Resolve the localized { title, message } for a notification.
 * Falls back to the stored (English) strings for unknown types.
 */
export function getNotificationContent(
  notification: Pick<Notification, "type" | "title" | "message" | "metadata">,
): { title: string; message: string } {
  const meta = notification.metadata || {};
  const titleKey = TITLE_KEYS[notification.type];
  const messageKey = MESSAGE_KEYS[notification.type];

  return {
    title: titleKey ? trans(titleKey) : notification.title || "",
    message: messageKey
      ? trans(messageKey, {
          title: meta.title ?? "",
          name: meta.name ?? "",
          group: meta.group ?? meta.communityGroupName ?? "",
          level: meta.level ?? "",
          rank: meta.rank ?? "",
          label: leagueLabel(meta),
          minutes: meta.minutes ?? "",
          dueCount: meta.dueCount ?? "",
          gameTitle: meta.gameTitle ?? "",
          diamondCost: meta.diamondCost ?? "",
        })
      : notification.message || "",
  };
}
